import { useCallback, useMemo, useState } from 'react';

import i18n from '@/i18n/config';
import { apiClient } from '@/utils/api';
import { buildApiParams } from '@/utils/helpers';

type ExportState = 'idle' | 'preparing' | 'confirm' | 'exporting';

export interface TableExportOptions<T> {
  resourceName: string;
  apiPath: string;
  /**
   * Current params atom value for this table.
   * We only care about page, per_page, filters and sort (if exists).
   */
  params: {
    page: number;
    per_page: number;
    filters: Record<string, string | undefined>;
    sort?: string;
  };
  /**
   * Latest meta coming from the list API.
   */
  meta:
    | {
        total: number;
        per_page: number;
        current_page: number;
        last_page: number;
      }
    | undefined;
  /**
   * Column definitions from the page (same as passed to DataTable)
   */
  columns: { name: string; uid: string }[];
  /**
   * Set of visible column uids as returned by useVisibleColumns
   */
  visibleColumns: Set<string>;
  /**
   * Function that maps a raw item to a flat record of values by column uid.
   * This keeps export independent from table renderers.
   */
  mapItemToRow: (item: T) => Record<string, any>;
  /**
   * Whether there is any active filter/sort on this page
   */
  hasAnyFilter: boolean;
}

export interface UseTableExportResult {
  exportState: ExportState;
  isExporting: boolean;
  needsConfirmation: boolean;
  totalRecords: number;
  handleRequestExport: () => void;
  handleConfirmExport: () => void;
  handleCancelExport: () => void;
}

const EXPORT_THRESHOLD = 100;
const EXPORT_PER_PAGE = 50;

function escapeCsvValue(value: any): string {
  if (value === null || value === undefined) return '';

  const str = String(value);
  if (str === '') return '';

  const needsEscape = /[",\n\r]/.test(str);
  const cleaned = str.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  if (!needsEscape) return cleaned;

  return `"${cleaned.replace(/"/g, '""')}"`;
}

function buildCsv<T>(opts: {
  items: T[];
  columns: { name: string; uid: string }[];
  visibleColumns: Set<string>;
  mapItemToRow: (item: T) => Record<string, any>;
}): string {
  const { items, columns, visibleColumns, mapItemToRow } = opts;

  const orderedColumns = columns.filter((c) => visibleColumns.has(c.uid));
  const header = orderedColumns.map((c) => escapeCsvValue(c.name)).join(',');

  const rows = items.map((item) => {
    const rowObj = mapItemToRow(item);
    return orderedColumns
      .map((c) => {
        const raw = rowObj[c.uid];
        return escapeCsvValue(raw);
      })
      .join(',');
  });

  return [header, ...rows].join('\n');
}

function downloadCsv(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function buildFilename(resourceName: string, hasAnyFilter: boolean) {
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const time = `${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;

  const suffix = hasAnyFilter ? '_filtered' : '';
  return `${resourceName}_${date}_${time}${suffix}.csv`;
}

export function useTableExport<T>(options: TableExportOptions<T>): UseTableExportResult {
  const { resourceName, apiPath, params, meta, columns, visibleColumns, mapItemToRow, hasAnyFilter } = options;
  const [state, setState] = useState<ExportState>('idle');

  const totalRecords = meta?.total ?? 0;
  const needsConfirmation = totalRecords > EXPORT_THRESHOLD;

  const startExport = useCallback(async () => {
    if (!meta || totalRecords === 0) {
      const { toast } = await import('react-toastify');
      toast.info('No data found to export.');
      setState('idle');
      return;
    }

    setState('exporting');

    try {
      const { toast } = await import('react-toastify');
      toast.info('Preparing CSV export ...');

      const allItems: T[] = [];

      const totalPages = meta.last_page;

      for (let page = 1; page <= totalPages; page++) {
        const baseParams: Record<string, any> = {
          page,
          per_page: EXPORT_PER_PAGE
        };

        if (typeof params.sort === 'string' && params.sort) {
          baseParams.sort = params.sort;
        }

        const apiParams = buildApiParams(baseParams, params.filters || {});
        const { data } = await apiClient.get(apiPath, { params: apiParams });

        const pageItems: T[] = (data?.data as T[]) ?? [];
        allItems.push(...pageItems);
      }

      const csv = buildCsv<T>({
        items: allItems,
        columns,
        visibleColumns,
        mapItemToRow
      });

      const filename = buildFilename(resourceName, hasAnyFilter);
      downloadCsv(csv, filename);

      toast.success(i18n.t('copy.csv_export_generated_successfully'));
      setState('idle');
    } catch (error) {
      console.error('Export failed', error);
      const { toast } = await import('react-toastify');
      toast.error(i18n.t('errors.exportCsv'));
      setState('idle');
    }
  }, [apiPath, columns, hasAnyFilter, mapItemToRow, meta, params.filters, params.sort, totalRecords]);

  const handleRequestExport = useCallback(() => {
    if (!meta || totalRecords === 0) {
      // Will be handled in startExport
      startExport();
      return;
    }

    if (meta.total <= EXPORT_THRESHOLD) {
      setState('preparing');
      startExport();
      return;
    }

    setState('confirm');
  }, [meta, startExport, totalRecords]);

  const handleConfirmExport = useCallback(() => {
    setState('preparing');
    startExport();
  }, [startExport]);

  const handleCancelExport = useCallback(() => {
    setState('idle');
  }, []);

  const isExporting = state === 'preparing' || state === 'exporting';

  return {
    exportState: state,
    isExporting,
    needsConfirmation,
    totalRecords,
    handleRequestExport,
    handleConfirmExport,
    handleCancelExport
  };
}


